import { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, CircularProgress, Typography, Alert } from '@mui/material'
import { supabase } from '../utils/supabase'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { v4 as uuidv4 } from 'uuid'

// Import booking step components
import BookingStepWrapper from '../components/booking/BookingStepWrapper'
import ClientInfoStep from '../components/booking/ClientInfoStep'
import PetInfoStep from '../components/booking/PetInfoStep'
import ServiceSelectionStep from '../components/booking/ServiceSelectionStep'
import DateTimeStep from '../components/booking/DateTimeStep'
import ConfirmationStep from '../components/booking/ConfirmationStep'
import PaymentStep from '../components/booking/PaymentStep'
import SuccessScreen from '../components/booking/SuccessScreen'

// Import utilities
import { convertToUtcTimestamps, canEmployeePerformService, simulateResourceAllocation } from '../utils/appointmentUtils'
import { formatPhoneForDisplay } from '../utils/phoneUtils'
import { buildFullName } from '../utils/clientUtils'
import { getCurrencySymbol } from '../utils/currencyUtils'
import posthog, { isPostHogConfigured } from '../posthog'
import { logPostHogWorkflow } from '../posthogLogger'

dayjs.extend(utc)
dayjs.extend(timezone)

// Step names
const STEPS = {
	CLIENT_INFO: 1,
	PET_INFO: 2,
	SERVICE: 3,
	DATE_TIME: 4,
	CONFIRMATION: 5,
	PAYMENT: 6,
	SUCCESS: 'success'
}

export default function BookingPage() {
	const { slug } = useParams()
	const navigate = useNavigate()

	// Loading states
	const [loading, setLoading] = useState(true)
	const [booking, setBooking] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')

	// Merchant data
	const [merchant, setMerchant] = useState(null)
	const [services, setServices] = useState([])
	const [addOns, setAddOns] = useState([])
	const [employees, setEmployees] = useState([])
	const [categories, setCategories] = useState([])

	// Step navigation
	const [currentStep, setCurrentStep] = useState(STEPS.CLIENT_INFO)
	const [clientSubStep, setClientSubStep] = useState('phone') // 'phone' | 'name'
	const [serviceSubStep, setServiceSubStep] = useState('service') // 'service' | 'addons'

	// Client info state
	const [clientInfo, setClientInfo] = useState({
		phone: '',
		name: '',
		lastName: '',
		email: '',
		existingClientId: null
	})

	// Pet info state
	const [pets, setPets] = useState([{ name: '', type: null, breed: null, weight: '' }])
	const [currentPetIndex, setCurrentPetIndex] = useState(0)

	// Service selection state
	const [petServices, setPetServices] = useState([]) // [{ petIndex, serviceId, addOnIds[] }]

	// Date/time state
	const [selectedDate, setSelectedDate] = useState(null)
	const [selectedTime, setSelectedTime] = useState(null)
	const [selectedEmployee, setSelectedEmployee] = useState(null)

	// Agreements from the agreements table
	const [merchantAgreements, setMerchantAgreements] = useState([])

	// Signed agreement data: { [agreementId]: { signedAgreementId, storagePath } }
	const [signedAgreementData, setSignedAgreementData] = useState({})

	// Payment state is handled in handlePaymentSuccess

	// Load merchant data
	const loadMerchantData = useCallback(async () => {
		setLoading(true)
		setErrorMessage('')

		try {
			// Load merchant
			const { data: merchantData, error: merchantError } = await supabase
				.from('merchant')
				.select('id, name, address, phone, description, timezone, weekly_hours, closed_recurring_dates, active, is_landing_page_active, late_cancel_hours, late_cancel_fee_type, late_cancel_fee_value, no_show_fee_type, no_show_fee_value, currency')
				.eq('slug', slug)
				.eq('active', true)
				.maybeSingle()

			if (merchantError) throw merchantError
			if (!merchantData) {
				setErrorMessage('Store not found or not accepting bookings')
				setLoading(false)
				return
			}

			// Check if landing page is active
			if (merchantData.is_landing_page_active === false) {
				setErrorMessage('Store not found or not accepting bookings')
				setLoading(false)
				return
			}

			setMerchant(merchantData)

			// Load services (is_service = true) with category info
			const { data: servicesData, error: servicesError } = await supabase
				.from('service_types')
				.select('id, name, duration_minutes, category_id, appointment_color, price, description, categories(id, name)')
				.eq('merchant_id', merchantData.id)
				.eq('is_service', true)
				.order('category_id', { ascending: true })
				.order('name', { ascending: true })

			if (servicesError) throw servicesError
			setServices(servicesData || [])

			// Load categories for this merchant
			const { data: categoriesData, error: categoriesError } = await supabase
				.from('categories')
				.select('id, name')
				.eq('merchant_id', merchantData.id)
				.order('name')

			if (categoriesError) {
				console.error('Error loading categories:', categoriesError)
				setCategories([])
			} else {
				setCategories(categoriesData || [])
			}

			// Load add-ons (is_add_on = true)
			const { data: addOnsData, error: addOnsError } = await supabase
				.from('service_types')
				.select('id, name, duration_minutes, category_id, price, description')
				.eq('merchant_id', merchantData.id)
				.eq('is_add_on', true)
				.order('name')

			if (addOnsError) throw addOnsError
			setAddOns(addOnsData || [])

			// Load employees
			const { data: employeesData, error: employeesError } = await supabase
				.from('employees')
				.select('id, first_name, last_name, services')
				.eq('merchant_id', merchantData.id)
				.eq('active', true)

			if (employeesError) {
				console.error('Error loading employees:', employeesError)
			} else {
				setEmployees(employeesData || [])
			}

			// Load enabled agreements from the agreements table
			const { data: agreementsData } = await supabase
				.from('agreements')
				.select('id, title, content, content_path, frequency, enabled, required_for_existing_clients')
				.eq('merchant_id', merchantData.id)
				.eq('enabled', true)
				.is('deleted_at', null)
				.order('created_at', { ascending: true })

			setMerchantAgreements(agreementsData || [])

		} catch (e) {
			console.error('Error loading merchant data:', e)
			setErrorMessage(e.message || 'Failed to load store information')
		} finally {
			setLoading(false)
		}
	}, [slug])

	useEffect(() => {
		if (slug) {
			loadMerchantData()
		}
	}, [slug, loadMerchantData])

	// Calculate total duration
	const totalDuration = useMemo(() => {
		let duration = 0
		petServices.forEach(ps => {
			// Add service duration
			const service = services.find(s => s.id === ps.serviceId)
			if (service) {
				duration += service.duration_minutes || 0
			}

			// Add add-ons durations
			(ps.addOnIds || []).forEach(addOnId => {
				const addOn = addOns.find(a => a.id === addOnId)
				if (addOn) {
					duration += addOn.duration_minutes || 0
				}
			})
		})
		return duration
	}, [petServices, services, addOns])

	// Handle existing client found
	const handleExistingClientFound = (client) => {
		setClientInfo(prev => ({
			...prev,
			existingClientId: client.id
		}))
	}

	// Handle existing pets found
	const handleExistingPetsFound = (existingPets) => {
		if (existingPets.length > 0) {
			const mappedPets = existingPets.map(p => ({
				id: p.id,
				name: p.name,
				type: p.species,
				breed: p.breed,
				weight: p.weight?.toString() || ''
			}))
			setPets(mappedPets)
		}
	}

	// Validate current step
	const validateStep = () => {
		setErrorMessage('')

		switch (currentStep) {
			case STEPS.CLIENT_INFO:
				if (clientSubStep === 'phone') {
					if (!clientInfo.phone || clientInfo.phone.length !== 10) {
						setErrorMessage('Please enter a valid 10-digit phone number')
						return false
					}
					if (!clientInfo.consent) {
						setErrorMessage('Please check the box to consent before your appointment.')
						return false
					}
					return true
				} else {
					if (!clientInfo.name?.trim()) {
						setErrorMessage('Please enter your name')
						return false
					}
					if (!clientInfo.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientInfo.email)) {
						setErrorMessage('Please enter a valid email address')
						return false
					}
					return true
				}

			case STEPS.PET_INFO:
				const currentPet = pets[currentPetIndex]
				if (!currentPet?.name?.trim()) {
					setErrorMessage('Please enter your pet\'s name')
					return false
				}
				if (!currentPet?.type) {
					setErrorMessage('Please select a pet type')
					return false
				}
				return true

			case STEPS.SERVICE:
				const currentPetService = petServices.find(ps => ps.petIndex === currentPetIndex)
				if (!currentPetService?.serviceId) {
					setErrorMessage('Please select a service')
					return false
				}
				return true

			case STEPS.DATE_TIME:
				if (!selectedDate) {
					setErrorMessage('Please select a date')
					return false
				}
				if (!selectedTime) {
					setErrorMessage('Please select a time')
					return false
				}
				return true

			default:
				return true
		}
	}

	// Handle next step
	const handleNext = async () => {
		if (!validateStep()) return

		setErrorMessage('')

		// Special handling for client info step
		if (currentStep === STEPS.CLIENT_INFO) {
			if (clientSubStep === 'phone') {
				// Just move to name/email step
				setClientSubStep('name')
				return
			} else {
				// Check for existing client using both phone AND email
				const normalizedEmail = clientInfo.email.trim().toLowerCase()

				// Format phone number for display (e.g., "(778) 680-0253")
				const formattedPhone = formatPhoneForDisplay(clientInfo.phone)

				try {
				const { data: existingClient } = await supabase
					.from('clients')
					.select('id, name, email, initial_agreement_signed, signed_one_time_agreement_ids')
					.eq('merchant_id', merchant.id)
					.eq('phone_number', formattedPhone)
					.ilike('email', normalizedEmail)
					.maybeSingle()

				if (existingClient) {
					setClientInfo(prev => ({
						...prev,
						existingClientId: existingClient.id,
						initialAgreementSigned: existingClient.initial_agreement_signed || false,
						signedOneTimeAgreementIds: existingClient.signed_one_time_agreement_ids || [],
					}))

						// Fetch existing pets
						const { data: existingPets } = await supabase
							.from('pets')
							.select('id, name, species, breed, weight')
							.eq('client_id', existingClient.id)
							.order('name')

						if (existingPets?.length > 0) {
							handleExistingPetsFound(existingPets)
						}
					} else {
						// New client - clear any existing client ID
						setClientInfo(prev => ({
							...prev,
							existingClientId: null
						}))
					}
				} catch (err) {
					console.error('Error checking existing client:', err)
				}

				// Move to pet info step
				setCurrentStep(STEPS.PET_INFO)
				return
			}
		}

		// Pet info step - handle multiple pets
		if (currentStep === STEPS.PET_INFO) {
			if (currentPetIndex < pets.length - 1) {
				// Move to next pet
				setCurrentPetIndex(prev => prev + 1)
				return
			} else {
				// All pets done, move to service selection
				setCurrentStep(STEPS.SERVICE)
				setCurrentPetIndex(0) // Reset for service selection
				setServiceSubStep('service') // Start with service selection
				return
			}
		}

		// Service step - handle service and add-ons sub-steps
		if (currentStep === STEPS.SERVICE) {
			if (serviceSubStep === 'service') {
				// Service selected, move to add-ons if available
				if (addOns.length > 0) {
					setServiceSubStep('addons')
					return
				}
				// No add-ons, check if more pets need service selection
			}

			// In addons step or no addons available
			if (currentPetIndex < pets.length - 1) {
				// Move to next pet for service selection
				setCurrentPetIndex(prev => prev + 1)
				setServiceSubStep('service') // Reset to service selection for next pet
				return
			} else {
				// All pets have services, move to date/time
				setCurrentStep(STEPS.DATE_TIME)
				return
			}
		}

		// Date/time step
		if (currentStep === STEPS.DATE_TIME) {
			setCurrentStep(STEPS.CONFIRMATION)
			return
		}

		// Confirmation step
		if (currentStep === STEPS.CONFIRMATION) {
			// Skip payment step if merchant has no cancellation/no-show policy
			const hasNoPolicy = !merchant?.late_cancel_fee_type && !merchant?.no_show_fee_type
			if (hasNoPolicy) {
				// Create bookings directly without payment info
				createBookings(null, null)
				return
			}
			setCurrentStep(STEPS.PAYMENT)
			return
		}
	}

	// Handle back step
	const handleBack = () => {
		setErrorMessage('')

		// Client info step
		if (currentStep === STEPS.CLIENT_INFO) {
			if (clientSubStep === 'name') {
				setClientSubStep('phone')
				return
			}
			// Can't go back from phone step - could navigate away
			return
		}

		// Pet info step
		if (currentStep === STEPS.PET_INFO) {
			if (currentPetIndex > 0) {
				setCurrentPetIndex(prev => prev - 1)
				return
			} else {
				setCurrentStep(STEPS.CLIENT_INFO)
				setClientSubStep('name')
				return
			}
		}

		// Service step
		if (currentStep === STEPS.SERVICE) {
			// If in addons sub-step, go back to service selection
			if (serviceSubStep === 'addons') {
				setServiceSubStep('service')
				return
			}

			// In service sub-step
			if (currentPetIndex > 0) {
				// Go to previous pet's add-ons (if they have add-ons) or service
				setCurrentPetIndex(prev => prev - 1)
				setServiceSubStep(addOns.length > 0 ? 'addons' : 'service')
				return
			} else {
				setCurrentStep(STEPS.PET_INFO)
				setCurrentPetIndex(pets.length - 1)
				return
			}
		}

		// Date/time step
		if (currentStep === STEPS.DATE_TIME) {
			setCurrentStep(STEPS.SERVICE)
			setCurrentPetIndex(pets.length - 1)
			setServiceSubStep(addOns.length > 0 ? 'addons' : 'service')
			return
		}

		// Confirmation step
		if (currentStep === STEPS.CONFIRMATION) {
			setCurrentStep(STEPS.DATE_TIME)
			return
		}

		// Payment step
		if (currentStep === STEPS.PAYMENT) {
			setCurrentStep(STEPS.CONFIRMATION)
			return
		}
	}

	// Handle payment success
	const handlePaymentSuccess = (paymentData) => {
		// Proceed to create bookings with payment data
		createBookings(paymentData.paymentMethodId, paymentData.customerId)
	}

	// Handle payment error
	const handlePaymentError = (error) => {
		console.error('Payment error:', error)
		setErrorMessage(error.message || 'Payment setup failed. Please try again.')
	}

	// Create bookings
	const createBookings = async (pmId, stripeId) => {
		setBooking(true)
		setErrorMessage('')

		try {
			// Step 1: Create or find client, do not update client info
			let clientId = clientInfo.existingClientId

			if (!clientId) {
				// Create new client
				const { data: newClient, error: clientError } = await supabase
					.from('clients')
					.insert({
						merchant_id: merchant.id,
						name: clientInfo.name.trim(),
						last_name: clientInfo.lastName?.trim() || null,
						email: clientInfo.email.trim(),
						phone_number: formatPhoneForDisplay(clientInfo.phone)
					})
					.select('id')
					.single()

				if (clientError) throw clientError
				clientId = newClient.id
			}
			// Step 2: Create or update pets
			const petIdMap = {} // petIndex -> petId

			for (let i = 0; i < pets.length; i++) {
				const pet = pets[i]

				if (pet.id) {
					// Update existing pet
					await supabase
						.from('pets')
						.update({
							name: pet.name.trim(),
							species: pet.type,
							breed: pet.breed,
							weight: pet.weight ? parseFloat(pet.weight) : null
						})
						.eq('id', pet.id)

					petIdMap[i] = pet.id
				} else {
					// Create new pet
					const { data: newPet, error: petError } = await supabase
						.from('pets')
						.insert({
							client_id: clientId,
							name: pet.name.trim(),
							species: pet.type,
							breed: pet.breed,
							weight: pet.weight ? parseFloat(pet.weight) : null
						})
						.select('id')
						.single()

					if (petError) throw petError
					petIdMap[i] = newPet.id
				}
			}

			// Step 3: Create appointments
			// Generate group_id if multiple pets
			const groupId = pets.length > 1 ? uuidv4() : null

			// Fetch existing appointments for availability check
			const { utcStart, utcEnd } = convertToUtcTimestamps(
				selectedDate.format('YYYY-MM-DD'),
				selectedTime,
				merchant.timezone,
				totalDuration
			)

			const { data: existingAppointments } = await supabase
				.from('appointments')
				.select('service_id, employee_id, start_ts, end_ts')
				.eq('merchant_id', merchant.id)
				.in('status', ['booked', 'bookPending'])
				.lt('start_ts', utcEnd)
				.gt('end_ts', utcStart)

			// Check availability
			const serviceIds = petServices.map(ps => ps.serviceId).filter(Boolean)
			const availableCapacity = simulateResourceAllocation(
				serviceIds[0],
				utcStart,
				utcEnd,
				existingAppointments || [],
				employees,
				services,
				merchant
			)

			if (availableCapacity <= 0) {
				throw new Error('This time slot is no longer available. Please select a different time.')
			}

			// Find assigned employee
			let assignedEmployeeId = selectedEmployee

			if (!assignedEmployeeId) {
				// Find available employee who can perform all services
				const busyEmployeeIds = new Set(
					existingAppointments?.map(apt => apt.employee_id).filter(Boolean) || []
				)

				const availableEmployees = employees.filter(emp =>
					serviceIds.every(sid => canEmployeePerformService(emp, sid)) &&
					!busyEmployeeIds.has(emp.id)
				)

				if (availableEmployees.length > 0) {
					const randomIndex = Math.floor(Math.random() * availableEmployees.length)
					assignedEmployeeId = availableEmployees[randomIndex].id
				}
			}

			// Create appointments
			const appointments = []
			let currentStartTime = dayjs.tz(
				`${selectedDate.format('YYYY-MM-DD')} ${selectedTime}`,
				merchant.timezone
			)

			for (let i = 0; i < petServices.length; i++) {
				const ps = petServices[i]
				const service = services.find(s => s.id === ps.serviceId)
				if (!service) continue

				// Calculate duration for this pet's service + add-ons
				let petDuration = service.duration_minutes || 0
				const addOnsData = (ps.addOnIds || []).map(addOnId => {
					const addOn = addOns.find(a => a.id === addOnId)
					if (addOn) {
						petDuration += addOn.duration_minutes || 0
						return {
							service_id: addOn.id,
							name: addOn.name,
							price: addOn.price,
							duration_minutes: addOn.duration_minutes || 0
						}
					}
					return null
				}).filter(Boolean)

				const petUtcStart = currentStartTime.utc().toISOString()
				const petUtcEnd = currentStartTime.add(petDuration, 'minute').utc().toISOString()

				const appointment = {
					merchant_id: merchant.id,
					service_id: ps.serviceId,
					employee_id: assignedEmployeeId,
					user_id: clientId,
					pet_id: petIdMap[ps.petIndex],
					start_ts: petUtcStart,
					end_ts: petUtcEnd,
					status: 'bookPending',
					customer_name: buildFullName(clientInfo.name.trim(), clientInfo.lastName?.trim()),
					customer_email: clientInfo.email.trim(),
					phone_number: formatPhoneForDisplay(clientInfo.phone),
					add_ons: addOnsData,
					stripe_customer_id: stripeId,
					stripe_payment_method_id: pmId,
					group_id: groupId,
					is_group_primary: i === 0,
				}

				appointments.push(appointment)

				// Next appointment starts after this one
				currentStartTime = currentStartTime.add(petDuration, 'minute')
			}

			// Insert all appointments and get back their IDs
			const { data: insertedAppointments, error: appointmentError } = await supabase
				.from('appointments')
				.insert(appointments)
				.select('id, is_group_primary')

			if (appointmentError) throw appointmentError

			// Use the primary appointment ID (first pet in group) for the agreement link
			const primaryAppointmentId = insertedAppointments?.find(a => a.is_group_primary)?.id
				?? insertedAppointments?.[0]?.id
				?? null

			// Insert signed_agreements rows last (client + appointments must exist first)
			const signedEntries = Object.entries(signedAgreementData)
			if (signedEntries.length > 0) {
				const signedRows = signedEntries.map(([agreementId, { storagePath }]) => ({
					merchant_id: merchant.id,
					agreement_id: agreementId,
					client_id: clientId,
					appointment_id: primaryAppointmentId,
					signature_path: storagePath,
					status: 'signed',
					signed_at: new Date().toISOString(),
				}))
				const { error: sigErr } = await supabase.from('signed_agreements').insert(signedRows)
				if (sigErr) console.error('Failed to insert signed_agreements:', sigErr.message, sigErr.details, sigErr.hint)
			}

			// Update client: set initial_agreement_signed and rebuild signed_one_time_agreement_ids
			const activeOnceIds = new Set(
				merchantAgreements.filter(a => a.frequency === 'once').map(a => a.id)
			)
			const newlySignedOnceIds = signedEntries
				.map(([agId]) => agId)
				.filter(agId => activeOnceIds.has(agId))
			// Keep only IDs that still belong to active agreements, then merge newly signed ones
			const pruned = (clientInfo.signedOneTimeAgreementIds || []).filter(id => activeOnceIds.has(id))
			const merged = [...new Set([...pruned, ...newlySignedOnceIds])]
			await supabase.from('clients').update({
				initial_agreement_signed: true,
				signed_one_time_agreement_ids: merged,
			}).eq('id', clientId)

			// Success!
			if (isPostHogConfigured) {
				const bookingLogAttributes = {
					event: 'booking_requested',
					appointment_count: appointments.length,
					service_count: new Set(petServices.map(({ serviceId }) => serviceId).filter(Boolean)).size,
					has_payment_method: Boolean(pmId),
					agreements_signed_count: signedEntries.length,
				}
				posthog.capture('booking_requested', {
					appointment_count: appointments.length,
					service_count: new Set(petServices.map(({ serviceId }) => serviceId).filter(Boolean)).size,
					has_payment_method: Boolean(pmId),
					agreements_signed_count: signedEntries.length,
				})
				logPostHogWorkflow('booking requested', bookingLogAttributes)
			}
			setCurrentStep(STEPS.SUCCESS)

		} catch (e) {
			console.error('Booking error:', e)
			setErrorMessage(e.message || 'Failed to create booking. Please try again.')
		} finally {
			setBooking(false)
		}
	}

	// Back to the merchant's own landing page (where the booking started),
	// not the Nami marketing home.
	const handleReturnHome = () => {
		navigate(slug ? `/store/${slug}` : '/')
	}

	// Get step title
	const getStepTitle = () => {
		switch (currentStep) {
			case STEPS.CLIENT_INFO:
				return clientSubStep === 'phone' ? null : 'Enter your information'
			case STEPS.PET_INFO:
				return null // Handled in step component
			case STEPS.SERVICE:
				return null // Handled in step component
			case STEPS.DATE_TIME:
				return null // Handled in step component
			case STEPS.CONFIRMATION:
				return null // Handled in step component
			case STEPS.PAYMENT:
				return null // Handled in step component
			default:
				return null
		}
	}

	// Get next button label
	const getNextLabel = () => {
		if (currentStep === STEPS.PET_INFO && currentPetIndex < pets.length - 1) {
			return 'Add Next Pet'
		}
		if (currentStep === STEPS.PET_INFO && currentPetIndex === pets.length - 1) {
			return 'Select a service'
		}
		if (currentStep === STEPS.SERVICE) {
			if (serviceSubStep === 'service' && addOns.length > 0) {
				return 'Continue'
			}
			if (currentPetIndex < pets.length - 1) {
				return 'Select for Next Pet'
			}
			return 'Select a time'
		}
		if (currentStep === STEPS.CONFIRMATION) {
			return null // Request Booking button is in the component
		}
		return 'Next'
	}

	// Check if we should show next button
	const showNextButton = () => {
		return currentStep !== STEPS.CONFIRMATION && currentStep !== STEPS.PAYMENT
	}

	// Show back button
	const showBackButton = () => {
		return !(currentStep === STEPS.CLIENT_INFO && clientSubStep === 'phone')
	}

	// Loading state
	if (loading) {
		return (
			<Box sx={{
				minHeight: '100%',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				background: 'linear-gradient(180deg, #F7F6F3 37%, #F1E9FC 100%)'
			}}>
				<CircularProgress sx={{ color: '#9F7AEA' }} />
				<Typography sx={{ mt: 2, color: '#4A5568' }}>Loading...</Typography>
			</Box>
		)
	}

	// Error state (no merchant)
	if (!merchant) {
		return (
			<Box sx={{
				minHeight: '100%',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '24px',
				background: 'linear-gradient(180deg, #F7F6F3 37%, #F1E9FC 100%)'
			}}>
				<Alert severity="error" sx={{ maxWidth: '400px' }}>
					{errorMessage || 'Store not found'}
				</Alert>
			</Box>
		)
	}

	// Success screen
	if (currentStep === STEPS.SUCCESS) {
		return <SuccessScreen onReturnHome={handleReturnHome} merchantName={merchant?.name} />
	}

	// Main booking flow
	return (
		<BookingStepWrapper
			currentStep={currentStep}
			title="Book appointment"
			subtitle={getStepTitle()}
			onBack={handleBack}
			onNext={handleNext}
			showBack={showBackButton()}
			showNext={showNextButton()}
			nextLabel={getNextLabel()}
			nextDisabled={booking}
			loading={booking}
			maxWidth={currentStep === STEPS.DATE_TIME ? 900 : currentStep === STEPS.CONFIRMATION ? 700 : 582}
			hideNavigation={currentStep === STEPS.CONFIRMATION || currentStep === STEPS.PAYMENT}
			showProgress={currentStep !== STEPS.CONFIRMATION && currentStep !== STEPS.PAYMENT}
			scrollKey={`${currentStep}-${serviceSubStep}-${clientSubStep}-${currentPetIndex}`}
		>
			{/* Error Alert */}
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			{/* Step Content */}
			{currentStep === STEPS.CLIENT_INFO && (
				<ClientInfoStep
					clientInfo={clientInfo}
					onClientInfoChange={setClientInfo}
					onExistingClientFound={handleExistingClientFound}
					onExistingPetsFound={handleExistingPetsFound}
					merchant={merchant}
					subStep={clientSubStep}
					onSubStepChange={setClientSubStep}
				/>
			)}

			{currentStep === STEPS.PET_INFO && (
				<PetInfoStep
					pets={pets}
					onPetsChange={setPets}
					currentPetIndex={currentPetIndex}
					onCurrentPetIndexChange={setCurrentPetIndex}
				/>
			)}

			{currentStep === STEPS.SERVICE && (
				<ServiceSelectionStep
					pets={pets}
					services={services}
					addOns={addOns}
					categories={categories}
					petServices={petServices}
					onPetServicesChange={setPetServices}
					currentPetIndex={currentPetIndex}
					onCurrentPetIndexChange={setCurrentPetIndex}
					subStep={serviceSubStep}
					onSubStepChange={setServiceSubStep}
					currencySymbol={getCurrencySymbol(merchant?.currency)}
				/>
			)}

			{currentStep === STEPS.DATE_TIME && (
				<DateTimeStep
					merchant={merchant}
					employees={employees}
					services={services}
					totalDuration={totalDuration}
					selectedDate={selectedDate}
					onDateChange={setSelectedDate}
					selectedTime={selectedTime}
					onTimeChange={setSelectedTime}
					selectedEmployee={selectedEmployee}
					onEmployeeChange={setSelectedEmployee}
					petServices={petServices}
				/>
			)}

			{currentStep === STEPS.CONFIRMATION && (
				<ConfirmationStep
					clientInfo={clientInfo}
					pets={pets}
					petServices={petServices}
					services={services}
					addOns={addOns}
					selectedDate={selectedDate}
					selectedTime={selectedTime}
					selectedEmployee={selectedEmployee}
					employees={employees}
					totalDuration={totalDuration}
					onRequestBooking={handleNext}
					onBack={handleBack}
					loading={booking}
					merchant={merchant}
					agreements={merchantAgreements}
					clientInitialAgreementSigned={clientInfo.initialAgreementSigned || false}
					signedOneTimeAgreementIds={clientInfo.signedOneTimeAgreementIds || []}
					merchantId={merchant?.id}
					clientId={clientInfo.existingClientId}
					onAgreementSigned={(agreementId, data) =>
						setSignedAgreementData(prev => ({ ...prev, [agreementId]: data }))
					}
				/>
			)}

			{currentStep === STEPS.PAYMENT && (
				<PaymentStep
					merchant={merchant}
					clientInfo={clientInfo}
					petServices={petServices}
					services={services}
					addOns={addOns}
					onPaymentSuccess={handlePaymentSuccess}
					onPaymentError={handlePaymentError}
					onBack={handleBack}
				/>
			)}
		</BookingStepWrapper>
	)
}
